import axios from 'axios';
import projectService from './projectService';
export * from './projectService';

// สร้าง axios instance
const apiInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interface สำหรับข้อมูล Dashboard
export interface DashboardStats {
  totalEmployees: string;
  workingEmployees: string;
  lateEmployees: string;
  absentEmployees: string;
}

export interface DashboardProject {
  id: number;
  projectCode: string;
  projectName: string;
  totalEmployees: number;
  workingEmployees: number;
  lateEmployees: number;
  absentEmployees: number;
  date: string;
}

export interface ProjectFilter {
  value: string;
  label: string;
}

export interface DashboardData {
  stats: DashboardStats;
  projects: DashboardProject[];
  filters: {
    projects: ProjectFilter[];
  };
}

// Interface สำหรับข้อมูลพนักงาน
export interface Employee {
  id: number;
  employeeCode: string;
  name: string;
  position: string;
  project: string;
  phone: string;
  email: string;
  status: string;
  workingTime?: {
    checkIn: string;
    checkOut: string;
  };
}

export interface EmployeeFormData {
  employeeCode: string;
  name: string;
  username: string;
  password: string;
  confirmPassword: string;
  phone: string;
  email: string;
  status: string;
}

export interface EmployeeFilters {
  projects: ProjectFilter[];
  status: ProjectFilter[];
  positions: ProjectFilter[];
}

export interface EmployeesData {
  employees: Employee[];
  filters: EmployeeFilters;
}

// Interface สำหรับข้อมูลการเข้า-ออกงาน
export interface CheckInOut {
  id: number;
  employeeCode: string;
  name: string;
  position: string;
  project: string;
  checkInTime: string;
  checkOutTime: string;
  status: string;
  checkInImage: string;
  checkOutImage: string;
  checkInLocation: string;
  checkOutLocation: string;
  checkinNote?: string;
  checkoutNote?: string;
}

export interface CheckInOutFilters {
  projects: ProjectFilter[];
  status: ProjectFilter[];
}

export interface CheckInOutData {
  employees: CheckInOut[];
  filters: CheckInOutFilters;
}

// Interface สำหรับข้อมูลโครงการ
export interface Project {
  id?: number;
  _id: string;
  name: string;
  description: string;
  isActive: boolean;
  workStartTime: string;
  workEndTime: string;
  maxLateTime: string;
  latitude: number;
  longitude: number;
  objectId: string;
  createdAt: string;
  updatedAt: string;
  // UI display properties
  projectCode?: string;
  projectName?: string;
  workingTime?: string;
  location?: string;
  status?: string;
}

export interface ProjectFormData {
  projectCode: string;
  projectName: string;
  workingTime: string;
  location: string;
  status: string;
  latitude: string;
  longitude: string;
  radius: string;
}

export interface ProjectsData {
  projects: Project[];
  filters: {
    status: ProjectFilter[];
  };
}

export interface ProjectEmployeeData {
  projectId: number;
  projectCode: string;
  projectName: string;
  employees: Employee[];
}

export interface PaginationParams {
  page: number;
  perPage: number;
}

export interface ProjectsResponse {
  data: Project[];
  meta: {
    total: number;
    page: number;
    perPage: number;
    totalPages: number;
  };
}

// API endpoints
export const dashboardAPI = {
  // ฟังก์ชันดึงข้อมูล Dashboard
  getDashboardData: async (): Promise<DashboardData> => {
    try {
      // ในสภาพแวดล้อมจริง จะใช้ API endpoint จริง
      // const response = await apiInstance.get('/api/dashboard');
      
      // สำหรับ development ใช้ mock data
      const response = await axios.get('/mocks/dashboard.json');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      throw error;
    }
  },

  // ฟังก์ชันค้นหาข้อมูล Dashboard
  searchDashboard: async (projectCode: string, date: string): Promise<DashboardData> => {
    try {
      // ในสภาพแวดล้อมจริง จะส่ง parameters ไปยัง API
      // const response = await apiInstance.get('/api/dashboard/search', {
      //   params: { projectCode, date }
      // });
      
      // สำหรับ development ใช้ mock data
      const response = await axios.get('/mocks/dashboard.json');
      const data = response.data;
      
      // จำลองการกรองข้อมูล
      if (projectCode) {
        data.projects = data.projects.filter(
          (project: DashboardProject) => project.projectCode === projectCode
        );
      }
      
      return data;
    } catch (error) {
      console.error('Error searching dashboard data:', error);
      throw error;
    }
  },
};

// API endpoints สำหรับพนักงาน
export const employeeAPI = {
  // ฟังก์ชันดึงข้อมูลพนักงานทั้งหมด
  getEmployees: async (): Promise<EmployeesData> => {
    try {
      const response = await axios.get('/mocks/employees.json');
      return response.data;
    } catch (error) {
      console.error('Error fetching employees data:', error);
      throw error;
    }
  },

  // ฟังก์ชันค้นหาพนักงาน
  searchEmployees: async (projectCode: string, status: string, keyword: string): Promise<EmployeesData> => {
    try {
      const response = await axios.get('/mocks/employees.json');
      const data = response.data;
      
      // จำลองการกรองข้อมูล
      let filteredEmployees = [...data.employees];
      
      if (projectCode) {
        filteredEmployees = filteredEmployees.filter(
          (employee: Employee) => employee.project.includes(projectCode)
        );
      }
      
      if (status) {
        filteredEmployees = filteredEmployees.filter(
          (employee: Employee) => employee.status === status
        );
      }
      
      if (keyword) {
        filteredEmployees = filteredEmployees.filter(
          (employee: Employee) => 
            employee.name.includes(keyword) || 
            employee.employeeCode.includes(keyword)
        );
      }
      
      return {
        ...data,
        employees: filteredEmployees
      };
    } catch (error) {
      console.error('Error searching employees data:', error);
      throw error;
    }
  },

  // ฟังก์ชันเพิ่มพนักงาน
  addEmployee: async (employeeData: EmployeeFormData): Promise<Employee> => {
    try {
      // ในสภาพแวดล้อมจริง จะใช้ API endpoint จริง
      // const response = await apiInstance.post('/api/employees', employeeData);
      
      // สำหรับ development จำลองการเพิ่มข้อมูล
      return {
        id: Math.floor(Math.random() * 1000),
        employeeCode: employeeData.employeeCode,
        name: employeeData.name,
        position: "พนักงานใหม่",
        project: "ยังไม่ได้กำหนด",
        phone: employeeData.phone,
        email: employeeData.email,
        status: employeeData.status
      };
    } catch (error) {
      console.error('Error adding employee:', error);
      throw error;
    }
  },

  // ฟังก์ชันดึงข้อมูลการเข้า-ออกงานของพนักงาน
  getEmployeeCheckInOut: async (employeeId: number): Promise<CheckInOutData> => {
    try {
      const response = await axios.get('/mocks/checkinout.json');
      const data = response.data;
      
      // จำลองการกรองข้อมูลตาม employeeId
      const filteredCheckInOut = data.employees.filter(
        (record: CheckInOut) => record.id === employeeId
      );
      
      return {
        ...data,
        employees: filteredCheckInOut
      };
    } catch (error) {
      console.error('Error fetching employee check-in/out data:', error);
      throw error;
    }
  },

  // ฟังก์ชันดึงข้อมูลรายละเอียดการเข้า-ออกงาน
  getCheckInOutDetails: async (employeeId: number, date: string): Promise<CheckInOut | null> => {
    try {
      const response = await axios.get('/mocks/checkinout.json');
      const data = response.data;
      
      // จำลองการหาข้อมูลตาม employeeId
      const record = data.employees.find(
        (record: CheckInOut) => record.id === employeeId
      );
      
      return record || null;
    } catch (error) {
      console.error('Error fetching check-in/out details:', error);
      throw error;
    }
  }
};

// Export the projectAPI from projectService
export const projectAPI = projectService;

export default apiInstance; 